<template>
  <v-card>
    <v-card-title class="font-weight-bold d-flex align-center justify-space-between">
      <div>
        <v-progress-circular v-if="loading" indeterminate size="20" class="mr-2"></v-progress-circular>
        {{ $t("brandPresets") }}
      </div>
      <v-btn icon @click="brandAssetStore.manageAssets = false">
        <v-icon>mdi-close-circle</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text>
      <FilePond
        ref="pond"
        :files="files"
        acceptedFileTypes="image/jpeg, image/png"
        :allowMultiple="true"
        :maxFiles="10"
        maxFileSize="2MB"
        :allowImagePreview="true"
        :imagePreviewMaxHeight="128"
        :filePosterMaxHeight="128"
        itemInsertLocation="after"
        credits="false"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { useAccountStore } from "@/stores/account";
import { useBrandAssetStore } from "@/stores/brand-asset";
import { useSnackbarStore } from "@/stores/snackbar";

import vueFilePond, { setOptions } from "vue-filepond";

import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import FilePondPluginFilePoster from "filepond-plugin-file-poster";

import "filepond/dist/filepond.min.css";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css";
import "filepond-plugin-file-poster/dist/filepond-plugin-file-poster.css";

const FilePond = vueFilePond(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginFilePoster
);

export default {
  name: "BrandAssetsCard",
  components: { FilePond },
  setup() {
    const accountStore = useAccountStore();
    const brandAssetStore = useBrandAssetStore();
    const snackbarStore = useSnackbarStore();
    return { accountStore, brandAssetStore, snackbarStore };
  },
  data() {
    return {
      files: [],
      loading: true,
    };
  },
  async created() {
    try {
      if (this.accountStore.isLoggedIn && !this.brandAssetStore.lastSynced)
        await this.brandAssetStore.fetchBrandAssets();
    } catch (err) {
      console.log(err);
      this.snackbarStore.text = this.$t("failedToLoadBrandAssets");
      this.snackbarStore.snackbar = true;
    }
  },
  async mounted() {
    setOptions({
      server: {
        process: (fieldName, file, metadata, load, error, progress, abort) => {
          const controller = new AbortController();
          let brandAssetId;

          (async () => {
            try {
              const resp = await this.brandAssetStore.uploadBrandAsset({
                name: file.name,
                type: file.type,
                size: file.size,
              });
              const presignedPost = resp.data.presignedPost;
              const brandAsset = resp.data.brandAsset;
              brandAssetId = brandAsset._id;

              const formData = new FormData();
              Object.keys(presignedPost.fields).forEach((key) => formData.append(key, presignedPost.fields[key]));
              formData.append("file", file);

              await this.$http.request({
                method: "POST",
                url: presignedPost.url,
                data: formData,
                withCredentials: false,
                headers: {
                  "x-auth": null,
                },
                onUploadProgress: (p) => {
                  progress(true, p.loaded, p.total);
                },
                signal: controller.signal,
              });

              load(brandAssetId);
              this.brandAssetStore.brandAssets.push(brandAsset);
            } catch (err) {
              error(this.$t("somethingWentWrong"));
            }
          })();

          return {
            abort: async () => {
              controller.abort();
              await this.brandAssetStore.removeBrandAsset(brandAssetId);
              abort();
            },
          };
        },
        load: async (source, load, error, progress, abort) => {
          const controller = new AbortController();

          (async () => {
            try {
              const resp = await this.$http.request({
                method: "GET",
                url: source,
                withCredentials: false,
                responseType: "blob",
                headers: {
                  "Cache-Control": "no-cache",
                  Pragma: "no-cache",
                  Expires: "0",
                  "x-auth": null,
                },
                onDownloadProgress: (p) => {
                  progress(true, p.loaded, p.total);
                },
                signal: controller.signal,
              });
              load(resp.data);
            } catch (err) {
              error(err);
            }
          })();

          return {
            abort: () => {
              controller.abort();
              abort();
            },
          };
        },
        revert: async (uniqueFileId, load, error) => {
          const resp = await this.brandAssetStore.removeBrandAsset(uniqueFileId);
          if (resp) load();
          else error(this.$t("somethingWentWrong"));
        },
        remove: async (source, load, error) => {
          const resp = await this.brandAssetStore.removeBrandAsset(source);
          if (resp) load();
          else error(this.$t("somethingWentWrong"));
        },
      },
    });

    this.setFilepondFiles();
  },
  methods: {
    async getImageData(imageUrl) {
      const res = await fetch(imageUrl);
      const blob = await res.blob();

      // Read the Blob as DataURL using the FileReader API
      const reader = new FileReader();

      // Convert to promise so we can use async/await
      const fileReaderPromise = new Promise((resolve) => {
        reader.onloadend = () => {
          resolve(reader.result);
        };
      });

      reader.readAsDataURL(blob);
      return await fileReaderPromise;
    },
    async filesToFilepondFiles() {
      const promises = this.brandAssetStore.brandAssets.map(async (asset) => {
        return {
          source: asset._id,
          options: {
            type: "local",
            file: {
              name: asset.file.name,
              size: asset.file.size,
              type: asset.file.type,
            },
            metadata: {
              poster: await this.getImageData(asset.file.url),
            },
          },
        };
      });
      return await Promise.all(promises);
    },
    async setFilepondFiles() {
      this.files = await this.filesToFilepondFiles();
      this.loading = false;
    },
  },
  // watch: {
  //   "brandAssetStore.brandAssets.length"() {
  //     this.setFilepondFiles();
  //   },
  // },
  // computed: {
  //   files() {
  //     return this.brandAssetStore.brandAssets.map(async (asset) => {
  //       return {
  //         source: asset._id,
  //         options: {
  //           type: "local",
  //           file: {
  //             name: asset.file.name,
  //             size: asset.file.size,
  //             type: asset.file.type,
  //           },
  //           metadata: {
  //             poster: await this.getImageData(asset.file.url),
  //           },
  //         },
  //       };
  //     });
  //   },
  // },
};
</script>

<style>
.filepond--root {
  margin-bottom: 0;
}

/* the text color of the drop label*/
.theme--dark .filepond--drop-label {
  color: white;
}

/* the background color of the filepond drop area */
.theme--dark .filepond--panel-root {
  /* background-color: #f0f0f0; */
  background-color: #252525;
}

/* the background color of the file and file panel (used when dropping an image) */
.filepond--item-panel {
  background-color: #555;
}

/* the background color of the drop circle */
.filepond--drip-blob {
  /* background-color: #0360ec; */
  background-color: var(--v-primary-base);
}

/* the background color of the black action buttons */
.filepond--file-action-button {
  background-color: rgba(0, 0, 0, 0.5);
}

/* the color of the focus ring */
.filepond--file-action-button:hover,
.filepond--file-action-button:focus {
  box-shadow: 0 0 0 0.125em rgba(255, 255, 255, 0.9);
}

/* error state color */
[data-filepond-item-state*="error"] .filepond--item-panel,
[data-filepond-item-state*="invalid"] .filepond--item-panel {
  background-color: var(--v-error-base);
}

[data-filepond-item-state="processing-complete"] .filepond--item-panel {
  /* background-color: #13e46d; */
  background-color: var(--v-primary-base);
}

.filepond--credits {
  display: none;
}
</style>
